import React, { useState } from 'react';
import { Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/common';
import { createListing } from '../../api/services';
import { useAuthentication, useNotifications } from '../../store/AppContext';
import { useUserProfile } from '../../hooks';
import { ListingForm, useListingForm, validateListingFormData } from '../../features/account/listingForm';
import {
  createListingPayload,
  deriveRoutePlannerPointsFromGeoJson,
  isSupportedGeoJson,
  parseGeoJsonFile,
} from '../../features/account/listingEditor';
import { AccountLayout } from '../../features/account/shell';

export function AddListingPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuthentication();
  const { showNotification } = useNotifications();
  const { data: profile, isLoading: isProfileLoading } = useUserProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPilot = currentUser?.membership?.USER_TYPE === 'PILOT_B2B_COMPANY';

  const {
    formData,
    setFormData,
    handleFieldChange,
    handleDescriptionChange,
    handleSelectChange,
    handleLocationChange,
    handleDateChange,
    handlePermissionChange,
    handleLocationSelect,
  } = useListingForm();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.geojson') && !file.name.endsWith('.json')) {
      showNotification('Nieprawidłowy format pliku. Wybierz plik .geojson lub .json', 'error');
      return;
    }

    try {
      const parsedJson = await parseGeoJsonFile(file);
      if (!isSupportedGeoJson(parsedJson)) {
        showNotification('Nieprawidłowy format GeoJSON.', 'error');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        route_geojson: parsedJson,
        route_points: deriveRoutePlannerPointsFromGeoJson(parsedJson),
      }));
      showNotification('Plik GeoJSON został wczytany pomyślnie.', 'success');
    } catch {
      showNotification('Błąd podczas odczytu pliku JSON.', 'error');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser || !profile) return;

    const validationError = validateListingFormData(formData, { enforceFutureDates: true });
    if (validationError) {
      showNotification(validationError, 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createListingPayload(formData, { ownerId: profile.id });
      await createListing(payload);
      showNotification('Ogłoszenie zostało dodane pomyślnie!', 'success');
      navigate('/account/my-listings');
    } catch (err: any) {
      showNotification(err.message || 'Wystąpił błąd podczas dodawania ogłoszenia.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isProfileLoading) {
    return (
      <AccountLayout pageTitle="Dodaj nowe ogłoszenie">
        <LoadingSpinner message="Sprawdzanie uprawnień profilu..." />
      </AccountLayout>
    );
  }

  if (isPilot) {
    return (
      <AccountLayout pageTitle="Dodaj nowe ogłoszenie">
        <Alert severity="error">
          Piloci nie mogą dodawać ogłoszeń. Ta funkcja jest dostępna tylko dla firm transportowych.
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/account/my-listings')} sx={{ mt: 2 }}>
          Wróć do moich ogłoszeń
        </Button>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout pageTitle="Dodaj nowe ogłoszenie">
      <ListingForm
        formData={formData}
        setFormData={setFormData}
        handleFieldChange={handleFieldChange}
        handleDescriptionChange={handleDescriptionChange}
        handleSelectChange={handleSelectChange}
        handleLocationChange={handleLocationChange}
        handleDateChange={handleDateChange}
        handlePermissionChange={handlePermissionChange}
        handleLocationSelect={handleLocationSelect}
        handleFileUpload={handleFileUpload}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel="Opublikuj ogłoszenie"
      />
    </AccountLayout>
  );
}
